import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { API, formatApiError } from "@/contexts/AuthContext";
import { PageHeader, PageBody, ErrorBlock } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Wand2, Download, Activity, Package, Lightbulb, Check, Library, Pencil,
  Sparkles, Globe, User, Image as ImageIcon, Film, Play, Volume2, AlertTriangle, Flame,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STAGE_LABEL = {
  queued: "Queued", scripting: "Writing script", tts: "Synthesising voice",
  footage: "Fetching footage", rendering: "Rendering MP4",
  heygen_submitted: "Submitted to HeyGen", heygen_rendering: "HeyGen rendering",
  download: "Downloading", done: "Done", error: "Error",
  needs_review: "Needs manual review",
};
const STAGE_ORDER = ["queued", "scripting", "tts", "footage", "rendering", "done"];

const GROWTH_TYPES = [
  { id: "did_you_know", label: "Did You Know" },
  { id: "pov", label: "POV" },
  { id: "story_time", label: "Story Time" },
  { id: "reaction", label: "Reaction" },
  { id: "list_video", label: "List Video" },
  { id: "trending_sound_remix", label: "Trending Sound Remix" },
];

const STYLES = [
  { id: "faceless", label: "Faceless", icon: Film },
  { id: "avatar", label: "Avatar", icon: User },
  { id: "slideshow", label: "Slideshow", icon: ImageIcon },
];

export default function GeneratePage() {
  const loc = useLocation();
  const presetProduct = loc.state?.product;
  const presetNiche = loc.state?.presetNiche;
  const presetHashtag = loc.state?.presetHashtag;
  const productIdeas = loc.state?.productIdeas || [];
  const presetIdea = loc.state?.presetIdea || null;

  const [mode, setMode] = useState("affiliate"); // affiliate | growth
  const [style, setStyle] = useState("faceless");
  // Affiliate input source: library | freeform | hot | source_url
  // Growth input source: niche | source_url
  const [source, setSource] = useState(presetProduct ? "library" : "freeform");
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState(presetProduct?.id || "");
  const [selectedIdeaIdx, setSelectedIdeaIdx] = useState(
    presetIdea && productIdeas.length > 0 ? productIdeas.indexOf(presetIdea) : -1);

  const [form, setForm] = useState({
    product_name: presetProduct?.title || presetIdea || "Wireless Earbuds Pro",
    product_description: presetProduct?.description
      || (presetHashtag ? `Trending under #${presetHashtag}` : "Noise-cancelling, 36h battery, budget price"),
    niche: presetNiche || "tech-gadgets",
  });
  // Growth-only state
  const [growthType, setGrowthType] = useState("did_you_know");
  const [growthHashtag, setGrowthHashtag] = useState(presetHashtag || "");

  // Source-URL state (shared across modes)
  const [sourceUrl, setSourceUrl] = useState("");
  const [extractLoading, setExtractLoading] = useState(false);
  const [sourceData, setSourceData] = useState(null);
  const [sourceErr, setSourceErr] = useState(null);
  const [pickedHook, setPickedHook] = useState(null);

  // Avatar/voice
  const [avatars, setAvatars] = useState([]);
  const [voices, setVoices] = useState([]);
  const [quota, setQuota] = useState(null);
  const [avatarId, setAvatarId] = useState("");
  const [voiceId, setVoiceId] = useState("");

  const [job, setJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  // Load avatars when style switches to avatar
  useEffect(() => {
    if (style !== "avatar" || avatars.length > 0) return;
    axios.get(`${API}/avatars`).then((r) => {
      setAvatars(r.data.avatars || []);
      setVoices(r.data.voices || []);
      setQuota(r.data.quota);
      if (!avatarId && r.data.avatars?.[0]) setAvatarId(r.data.avatars[0].id);
      if (!voiceId && r.data.voices?.[0]) setVoiceId(r.data.voices[0].id);
    }).catch(() => toast.error("Could not load HeyGen avatars"));
  }, [style, avatars.length]); // eslint-disable-line

  const extractSource = async () => {
    if (!sourceUrl) return;
    setExtractLoading(true); setSourceErr(null); setSourceData(null); setPickedHook(null);
    try {
      const r = await axios.post(`${API}/sources/extract`, { url: sourceUrl });
      setSourceData(r.data);
    } catch (e) {
      setSourceErr(e?.response?.data?.detail || "Couldn't read that URL — try another.");
    } finally { setExtractLoading(false); }
  };

  const heygenBlocked = style === "avatar" && quota && quota.remaining_seconds === 0
    && (quota.remaining_usd ?? 0) < 0.5;

  const submit = async (e) => {
    e.preventDefault();
    if (heygenBlocked) {
      toast.error("HeyGen credits exhausted — top up at heygen.com");
      return;
    }
    if (source === "source_url" && !pickedHook && !sourceData) {
      toast.error("Extract a source URL and pick a hook first");
      return;
    }
    setSubmitting(true); setJob(null);
    try {
      let url, body;
      if (mode === "growth") {
        url = `${API}/video/growth`;
        body = {
          growth_type: growthType,
          niche: form.niche || null,
          context_hook: source === "source_url"
            ? (pickedHook || (sourceData?.title || ""))
            : (growthHashtag ? `Trending under #${growthHashtag}` : null),
          style, avatar_id: style === "avatar" ? avatarId : null,
          voice_id: style === "avatar" ? voiceId : null,
        };
      } else if (style === "avatar") {
        url = `${API}/video/avatar`;
        body = {
          ...(source === "library" && productId ? { product_id: productId } : {}),
          product_name: form.product_name,
          product_description: source === "source_url" && sourceData
            ? `${pickedHook || sourceData.title}\n${(sourceData.body || "").slice(0, 800)}`
            : form.product_description,
          niche: form.niche, avatar_id: avatarId, voice_id: voiceId,
        };
      } else if (style === "slideshow") {
        url = `${API}/video/slideshow`;
        body = {
          ...(source === "library" && productId ? { product_id: productId } : {}),
          product_name: form.product_name,
          product_description: form.product_description, niche: form.niche,
        };
      } else {
        url = `${API}/video/faceless`;
        body = {
          ...(source === "library" && productId ? { product_id: productId } : {}),
          product_name: form.product_name,
          product_description: source === "source_url" && sourceData
            ? `${pickedHook || sourceData.title}\n${(sourceData.body || "").slice(0, 800)}`
            : form.product_description,
          niche: form.niche,
        };
      }
      const r = await axios.post(url, body);
      setJob(r.data);
      toast.success(`${mode === "growth" ? "Growth" : "Affiliate"} ${style} job queued`);
    } catch (e) {
      const data = e?.response?.data;
      toast.error(formatApiError(data?.detail) || data?.error || "Failed to start job");
      setJob({ stage: "error", error: data?.detail || data?.error || String(e) });
    } finally { setSubmitting(false); }
  };

  // Poll
  useEffect(() => {
    if (!job?.job_id || ["done", "error", "needs_review"].includes(job.stage)) return;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/video/jobs/${job.job_id}`);
        setJob(r.data);
      } catch (_) {}
    }, 2500);
    return () => clearInterval(id);
  }, [job?.job_id, job?.stage]);

  const stageIdx = job ? STAGE_ORDER.indexOf(job.stage) : -1;
  const videoUrl = job?.video_url ? `${BACKEND_URL}${job.video_url}` : null;

  const sourceTabs = mode === "growth"
    ? [["niche", "Niche/Hashtag", Flame], ["source_url", "From Source URL", Globe]]
    : [["library", "Library", Library], ["freeform", "Freeform", Pencil],
       ["hot", "What's Hot", Sparkles], ["source_url", "From Source URL", Globe]];

  // Normalize source for growth mode
  useEffect(() => {
    if (mode === "growth" && !["niche", "source_url"].includes(source)) setSource("niche");
    if (mode === "affiliate" && !["library", "freeform", "hot", "source_url"].includes(source))
      setSource("freeform");
  }, [mode]); // eslint-disable-line

  return (
    <>
      <PageHeader icon={Wand2} title="Generate Video"
        subtitle="LLM → TTS → footage → ffmpeg. Compliance-linted before render."
        testid="generate-header" />

      <PageBody>
        {/* Mode toggle */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="mode-toggle">
          {[
            { id: "affiliate", label: "Affiliate Mode", desc: "Product-attached, compliance-linted with #ad disclosure.",
              icon: "🎯", color: "emerald" },
            { id: "growth", label: "Growth Mode", desc: "Viral content, no product, no disclosure.",
              icon: "🌱", color: "green" },
          ].map((m) => {
            const active = mode === m.id;
            return (
              <button key={m.id} type="button" onClick={() => setMode(m.id)}
                data-testid={`mode-${m.id}`} data-selected={active ? "true" : "false"}
                className={`ap-panel p-4 text-left transition-all ${
                  active ? `border-${m.color}-500 ring-2 ring-${m.color}-500/40 bg-${m.color}-500/5`
                         : "border-zinc-700 hover:border-zinc-600"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl">{m.icon}</span>
                  <span className="text-sm font-semibold">{m.label}</span>
                  {active && <Check className="h-4 w-4 ml-auto text-emerald-400" />}
                </div>
                <div className="text-[11px] text-zinc-400">{m.desc}</div>
              </button>
            );
          })}
        </div>

        {/* Product angle picker (only when coming from Trends with ideas) */}
        {productIdeas.length > 0 && mode === "affiliate" && (
          <section className="ap-panel p-4" data-testid="angle-picker">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="h-4 w-4 text-amber-400" />
              <h2 className="text-sm font-semibold">Pick your angle</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {productIdeas.slice(0, 3).map((idea, i) => {
                const sel = selectedIdeaIdx === i;
                return (
                  <button key={i} type="button"
                    onClick={() => { setSelectedIdeaIdx(i); setForm({ ...form, product_name: idea }); toast.success(`Angle: ${idea}`); }}
                    data-testid={`angle-card-${i}`} data-selected={sel}
                    className={`text-left p-3 rounded border transition ${
                      sel ? "bg-emerald-500/10 border-emerald-500 ring-2 ring-emerald-500/40"
                          : "bg-zinc-900 border-zinc-700 hover:border-emerald-700/60"}`}>
                    <div className="text-[10px] mono text-zinc-500">ANGLE {i + 1}{sel ? " · picked" : ""}</div>
                    <div className="text-sm mt-1">{idea}</div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <form onSubmit={submit} className="ap-panel p-4 space-y-4" data-testid="video-form">
            {/* Source tabs */}
            <div className="flex flex-wrap gap-1 rounded-md border border-zinc-700 p-1" data-testid="source-tabs">
              {sourceTabs.map(([id, label, Icon]) => (
                <button key={id} type="button" onClick={() => setSource(id)}
                  data-testid={`source-tab-${id}`} data-selected={source === id}
                  className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1.5 ${
                    source === id ? "bg-emerald-500 text-zinc-950 font-semibold"
                                  : "text-zinc-300 hover:bg-zinc-800"}`}>
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>

            {/* Source-URL panel */}
            {source === "source_url" && (
              <div className="space-y-3" data-testid="source-url-panel">
                <Label className="text-zinc-300 text-xs">Article or Reddit URL</Label>
                <div className="flex gap-2">
                  <Input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)}
                    placeholder="https://en.wikipedia.org/wiki/Wireless_earbuds"
                    className="bg-zinc-900 border-zinc-700 text-zinc-100" data-testid="source-url-input" />
                  <Button type="button" onClick={extractSource} disabled={extractLoading || !sourceUrl}
                    className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
                    data-testid="source-extract-btn">
                    {extractLoading ? "Extracting…" : "Extract"}
                  </Button>
                </div>
                {sourceErr && <div className="text-xs text-rose-400" data-testid="source-error">{sourceErr}</div>}
                {sourceData && (
                  <div className="ap-panel-2 p-3 space-y-2" data-testid="source-preview">
                    <Badge variant="outline" className="border-zinc-700 text-zinc-300 mono text-[10px]">
                      {sourceData.kind}
                    </Badge>
                    <div className="text-sm font-semibold line-clamp-2">{sourceData.title}</div>
                    <div className="text-[11px] text-zinc-400 line-clamp-3">{sourceData.body}</div>
                    <div className="pt-1 space-y-1">
                      <div className="text-[10px] mono text-zinc-500">PICK A HOOK</div>
                      {(sourceData.suggested_hooks || []).map((h, i) => {
                        const sel = pickedHook === h;
                        return (
                          <button key={i} type="button" onClick={() => setPickedHook(h)}
                            data-testid={`source-hook-${i}`} data-selected={sel}
                            className={`block w-full text-left p-2 rounded text-xs border ${
                              sel ? "border-emerald-500 bg-emerald-500/10 text-emerald-200"
                                  : "border-zinc-700 hover:border-zinc-600 text-zinc-300"}`}>
                            {sel && "✓ "}{h}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Growth-mode niche/hashtag */}
            {mode === "growth" && source === "niche" && (
              <>
                <div>
                  <Label className="text-zinc-300 text-xs">Growth type</Label>
                  <Select value={growthType} onValueChange={setGrowthType}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-100" data-testid="growth-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      {GROWTH_TYPES.map((g) => (
                        <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Niche (optional)</Label>
                  <Input value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-zinc-100" data-testid="growth-niche-input" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Trending hashtag (optional)</Label>
                  <Input value={growthHashtag} onChange={(e) => setGrowthHashtag(e.target.value)}
                    placeholder="tiktokmademebuyit" className="bg-zinc-900 border-zinc-700 text-zinc-100"
                    data-testid="growth-hashtag-input" />
                </div>
              </>
            )}

            {/* Affiliate-mode inputs */}
            {mode === "affiliate" && source === "library" && (
              <div>
                <Label className="text-zinc-300 text-xs">Saved product</Label>
                <Select value={productId || "_none"} onValueChange={(v) => {
                  if (v === "_none") return setProductId("");
                  setProductId(v);
                  const p = products.find((x) => x.id === v);
                  if (p) setForm({ ...form, product_name: p.title, product_description: p.description });
                }}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-100" data-testid="product-select">
                    <SelectValue placeholder="Pick a saved product" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 max-h-[260px]">
                    <SelectItem value="_none">— none —</SelectItem>
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {mode === "affiliate" && ["freeform", "hot", "library"].includes(source) && (
              <>
                <div>
                  <Label className="text-zinc-300 text-xs">Product name</Label>
                  <Input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-zinc-100" data-testid="product-name-input" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Description</Label>
                  <Textarea rows={3} value={form.product_description}
                    onChange={(e) => setForm({ ...form, product_description: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-zinc-100" data-testid="product-description-input" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Niche</Label>
                  <Input value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-zinc-100" data-testid="niche-input" />
                </div>
              </>
            )}

            {/* Style picker */}
            <div>
              <Label className="text-zinc-300 text-xs">Style</Label>
              <div className="grid grid-cols-3 gap-1 rounded-md border border-zinc-700 p-1 mt-1" data-testid="style-picker">
                {STYLES.map((s) => {
                  const Icon = s.icon;
                  return (
                    <button key={s.id} type="button" onClick={() => setStyle(s.id)}
                      data-testid={`style-${s.id}`} data-selected={style === s.id}
                      className={`px-2 py-1.5 text-xs rounded inline-flex items-center justify-center gap-1.5 ${
                        style === s.id ? "bg-emerald-500 text-zinc-950 font-semibold" : "text-zinc-300 hover:bg-zinc-800"}`}>
                      <Icon className="h-3.5 w-3.5" /> {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Avatar/voice grids (only when style=avatar) */}
            {style === "avatar" && (
              <div className="space-y-3" data-testid="avatar-panel">
                {quota && (
                  <div className="flex items-center justify-between text-[11px] mono">
                    <Badge variant="outline" className="border-zinc-700 text-zinc-300">
                      HeyGen: {quota.remaining_seconds ?? "?"}s · ${quota.remaining_usd ?? 0}
                    </Badge>
                    {heygenBlocked && (
                      <div className="text-rose-400 inline-flex items-center gap-1" data-testid="heygen-exhausted">
                        <AlertTriangle className="h-3 w-3" /> Credits exhausted — top up at heygen.com
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <Label className="text-zinc-300 text-xs">Avatar ({avatars.length})</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1" data-testid="avatar-grid">
                    {avatars.slice(0, 24).map((a) => {
                      const sel = avatarId === a.id;
                      return (
                        <button key={a.id} type="button" onClick={() => setAvatarId(a.id)}
                          data-testid={`avatar-${a.id}`} data-selected={sel}
                          className={`relative aspect-[3/4] rounded overflow-hidden border-2 ${
                            sel ? "border-emerald-500 ring-2 ring-emerald-500/40" : "border-zinc-700 hover:border-zinc-500"}`}>
                          {a.preview_url ? <img src={a.preview_url} alt="" className="w-full h-full object-cover" />
                            : <div className="w-full h-full bg-zinc-900 flex items-center justify-center"><User className="h-6 w-6 text-zinc-700" /></div>}
                          <div className="absolute bottom-0 inset-x-0 bg-zinc-950/80 text-[9px] mono p-1 truncate">{a.name || "—"}</div>
                          {sel && <div className="absolute top-1 right-1 bg-emerald-500 text-zinc-950 rounded-full p-0.5"><Check className="h-3 w-3" /></div>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Voice ({voices.length})</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto pr-1" data-testid="voice-grid">
                    {voices.slice(0, 30).map((v) => {
                      const sel = voiceId === v.id;
                      return (
                        <button key={v.id} type="button" onClick={() => setVoiceId(v.id)}
                          data-testid={`voice-${v.id}`} data-selected={sel}
                          className={`flex items-center gap-1.5 p-2 rounded border text-left text-[10px] ${
                            sel ? "border-emerald-500 bg-emerald-500/10" : "border-zinc-700 hover:border-zinc-500"}`}>
                          <Volume2 className="h-3 w-3 text-zinc-500" />
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium">{v.name}</div>
                            <div className="text-zinc-500">{v.language} · {v.gender}</div>
                          </div>
                          {v.preview_url && (
                            <a href={v.preview_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                               className="text-emerald-400 hover:text-emerald-300" data-testid={`voice-preview-${v.id}`}>
                              <Play className="h-3 w-3" />
                            </a>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <Button type="submit" disabled={submitting || heygenBlocked || (job && !["done", "error", "needs_review"].includes(job.stage))}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
              data-testid="generate-video-btn">
              {submitting ? "Queuing…" : `Generate ${mode === "growth" ? "growth" : "affiliate"} video`}
            </Button>
          </form>

          {/* Status panel */}
          <div className="ap-panel p-4" data-testid="video-status-panel">
            {!job ? (
              <div className="h-full min-h-[260px] flex items-center justify-center text-zinc-500 text-sm">
                Submit the form to start a job.
              </div>
            ) : job.stage === "error" || job.error ? (
              <ErrorBlock data={job} testid="video-error" />
            ) : job.stage === "needs_review" ? (
              <div className="space-y-3 text-sm" data-testid="video-needs-review">
                <Badge className="bg-amber-500/10 text-amber-300 border-amber-700/60 border" variant="outline">
                  <AlertTriangle className="h-3 w-3 mr-1" /> Needs manual review
                </Badge>
                <div className="text-zinc-300">The script failed compliance checks after retry.</div>
                {job.compliance?.banned_terms_found?.length > 0 && (
                  <div className="text-[11px] text-rose-300 mono">Banned: {job.compliance.banned_terms_found.join(", ")}</div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-emerald-400 animate-pulse" />
                    <span className="text-sm font-medium">{STAGE_LABEL[job.stage] || job.stage}</span>
                  </div>
                  <span className="mono text-xs text-zinc-400">{job.progress ?? 0}%</span>
                </div>
                <Progress value={job.progress ?? 0} className="h-2 bg-zinc-900" />
                {job.compliance && (
                  <div className="text-[10px] mono text-emerald-400">✓ compliance score {job.compliance.score}</div>
                )}
                {job.script && (
                  <div className="ap-panel-2 p-3 text-xs space-y-1 mono">
                    <div className="text-emerald-300">▸ {job.script.hook}</div>
                    <div className="text-zinc-300">  {job.script.beat1}</div>
                    <div className="text-zinc-300">  {job.script.beat2}</div>
                    <div className="text-amber-300">▸ {job.script.cta}</div>
                  </div>
                )}
                {job.stage === "done" && videoUrl && (
                  <div className="space-y-2" data-testid="video-result">
                    <video controls src={videoUrl} className="w-full max-h-[520px] rounded border border-zinc-800 bg-black" />
                    <a href={videoUrl} download className="inline-flex items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300">
                      <Download className="h-4 w-4" /> Download MP4
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </PageBody>
    </>
  );
}
